import {assert} from 'Asserts';
import {Collaborator, DataCollaborator, Ownable, Owner, OwnerInfo} from 'auth/PermissionConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import notifications from 'notifications';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as StageRunActions from 'workflows/StageRunActions';

import * as WorkflowTemplateDAO from './dao/WorkflowTemplateDAO';
import * as WorkflowUtil from './util/WorkflowUtil';
import * as WorkflowActions from './WorkflowActions';
import {
  ExperimentalCondition,
  StageConfiguration,
  StageFlowNode,
  WorkflowDetails,
  WorkflowTemplate,
  WorkflowTemplateSummary,
  WorkflowTemplateVersion,
  WorkflowTemplateVersionDetails,
  WorkflowTemplateVersionSummary,
} from './WorkflowConstants';

export enum WorkflowTemplateActionType {
  WORKFLOW_WORKFLOW_TEMPLATE_VIEW_SCREEN_OPEN = 'WORKFLOW_WORKFLOW_TEMPLATE_VIEW_SCREEN_OPEN',
  WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED = 'WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED',
  WORKFLOW_WORKFLOW_TEMPLATE_VERSION_SUMMARY_FETCH_COMPLETED = 'WORKFLOW_WORKFLOW_TEMPLATE_VERSION_SUMMARY_FETCH_COMPLETED',
  WORKFLOW_SELECT_WORKFLOW_TEMPLATE_VERSION = 'WORKFLOW_SELECT_WORKFLOW_TEMPLATE_VERSION',
  WORKFLOW_WORKFLOW_TEMPLATE_FULL_FETCH_COMPLETED = 'WORKFLOW_WORKFLOW_TEMPLATE_FULL_FETCH_COMPLETED',
  WORKFLOW_ALL_WORKFLOW_TEMPLATES_FETCH_COMPLETED = 'WORKFLOW_ALL_WORKFLOW_TEMPLATES_FETCH_COMPLETED',
  WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED = 'WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED',
  WORKFLOW_STAGE_CONFIGURATION_FETCH_COMPLETED = 'WORKFLOW_STAGE_CONFIGURATION_FETCH_COMPLETED',
}

interface WorkflowTemplateViewScreenOpenAction {
  type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VIEW_SCREEN_OPEN;
  workflowTemplateId: string;
}

export function openWorkflowTemplateViewScreen(workflowTemplateId: string): void {
  getStore().dispatch({
    type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VIEW_SCREEN_OPEN,
    workflowTemplateId,
  });
}

interface WorkflowTemplateVersionDetailsFetchCompletedAction {
  type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED;
  workflowTemplateVersion: WorkflowTemplateVersion;
  stageConfigurations: {[id: string]: StageConfiguration};
  stageFlowNodes: {[id: string]: StageFlowNode};
}

export async function fetchWorkflowTemplateVersionDetails(workflowTemplateVersionId: string): Promise<void> {
  const {workflowTemplateVersion, stageConfigurations, stageFlowNodes} =
    await WorkflowTemplateDAO.fetchWorkflowTemplateVersionDetails(workflowTemplateVersionId);
  getStore().dispatch({
    type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED,
    workflowTemplateVersion,
    stageConfigurations,
    stageFlowNodes,
  });
}

interface WorkflowTemplateVersionSummaryFetchCompletedAction {
  type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_SUMMARY_FETCH_COMPLETED;
  workflowTemplateVersionSummary: WorkflowTemplateVersionSummary;
}

export function editWorkflowTemplateVersionMetadata(
  workflowTemplateVersionId: string,
  {description}: {description: string}
): void {
  LoadingScreen.show();
  WorkflowTemplateDAO.editWorkflowTemplateVersionMetadata(workflowTemplateVersionId, {description})
    .done(
      ({
        workflowTemplateVersionSummary,
      }: {
        workflowTemplateVersionSummary: WorkflowTemplateVersionSummary;
      }) => {
        getStore().dispatch({
          type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_SUMMARY_FETCH_COMPLETED,
          workflowTemplateVersionSummary,
        });
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

interface SelectWorkflowTemplateVersionAction {
  type: WorkflowTemplateActionType.WORKFLOW_SELECT_WORKFLOW_TEMPLATE_VERSION;
  workflowTemplateVersionId: string;
}

export function selectWorkflowTemplateVersion(workflowTemplateVersionId: string): void {
  getStore().dispatch({
    type: WorkflowTemplateActionType.WORKFLOW_SELECT_WORKFLOW_TEMPLATE_VERSION,
    workflowTemplateVersionId,
  });
  fetchWorkflowTemplateVersionDetails(workflowTemplateVersionId);
}

interface WorkflowTemplateFullFetchCompletedAction {
  type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_FULL_FETCH_COMPLETED;
  workflowTemplate: WorkflowTemplate;
}

export function fetchWorkflowTemplateAndLatestVersion(workflowTemplateId: string): void {
  WorkflowTemplateDAO.fetchWorkflowTemplate(workflowTemplateId)
    .done(({workflowTemplate}: {workflowTemplate: WorkflowTemplate}) => {
      getStore().dispatch({
        type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_FULL_FETCH_COMPLETED,
        workflowTemplate,
      });
      selectWorkflowTemplateVersion(WorkflowUtil.getLatestWorkflowTemplateVersion(workflowTemplate).id);
    })
    .fail(notifications.notifyError);
}

interface AllWorkflowTemplateFetchCompletedAction {
  type: WorkflowTemplateActionType.WORKFLOW_ALL_WORKFLOW_TEMPLATES_FETCH_COMPLETED;
  workflowTemplates: Array<WorkflowTemplate>;
}

export function fetchAllWorkflowTemplates(): JQueryPromise<WorkflowTemplateDAO.FetchAllWorkflowTemplatesResponse> {
  return WorkflowTemplateDAO.fetchAllWorkflowTemplates()
    .done(({workflowTemplates}: {workflowTemplates: Array<WorkflowTemplate>}) => {
      getStore().dispatch({
        type: WorkflowTemplateActionType.WORKFLOW_ALL_WORKFLOW_TEMPLATES_FETCH_COMPLETED,
        workflowTemplates,
      });
    })
    .fail(notifications.notifyError);
}

interface WorkflowDetailsFetchCompletedAction extends WorkflowDetails {
  type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED;
}

export function createStageConfiguration({
  workflowTemplateVersionId,
  stageName,
  entryTemplateName,
  stageConfigurationIdForEntryTemplate,
  experimentalConditions,
  index,
}: {
  workflowTemplateVersionId: string;
  stageName: string;
  entryTemplateName: string;
  stageConfigurationIdForEntryTemplate: string | null;
  experimentalConditions: Array<ExperimentalCondition>;
  index: number | null | undefined;
}): void {
  LoadingScreen.show();
  WorkflowTemplateDAO.createStageConfiguration({
    workflowTemplateVersionId,
    stageName,
    entryTemplateName,
    stageConfigurationIdForEntryTemplate,
    experimentalConditions,
    index,
  })
    .done(
      ({
        workflowTemplateVersion,
        stageConfigurations,
        stageFlowNodes,
        workflowDetails,
      }: WorkflowTemplateDAO.CreateStageConfigurationResponse) => {
        if (workflowDetails) {
          const {stageRuns, entryIdToStructuredData} = workflowDetails;
          StageRunActions.dispatchStageRunsFetchedAndFetchMissingSamples({
            stageRuns: Object.values(stageRuns),
            entryIdToStructuredData,
          });
          getStore().dispatch({
            type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED,
            ...workflowDetails,
          });
        }
        getStore().dispatch({
          type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED,
          workflowTemplateVersion,
          stageConfigurations,
          stageFlowNodes,
        });
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function deleteStageConfiguration(stageConfigurationId: string): void {
  LoadingScreen.show();
  WorkflowTemplateDAO.deleteStageConfiguration(stageConfigurationId)
    .done(
      ({
        workflowTemplateVersion,
        stageConfigurations,
        stageFlowNodes,
        workflowDetails,
        deletedEntryIds,
      }: WorkflowTemplateDAO.DeleteStageConfigurationResponse) => {
        if (workflowDetails) {
          const {stageRuns, entryIdToStructuredData} = workflowDetails;
          StageRunActions.dispatchStageRunsFetchedAndFetchMissingSamples({
            stageRuns: Object.values(stageRuns),
            entryIdToStructuredData,
          });
          getStore().dispatch({
            type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED,
            ...workflowDetails,
          });
        }
        getStore().dispatch({
          type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED,
          workflowTemplateVersion,
          stageConfigurations,
          stageFlowNodes,
        });
        FileBrowserActions.bulkRemoveFromWorkspace(deletedEntryIds);
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

interface StageConfigurationFetchCompletedAction {
  type: WorkflowTemplateActionType.WORKFLOW_STAGE_CONFIGURATION_FETCH_COMPLETED;
  stageConfiguration: StageConfiguration;
}

export function editStageConfiguration(
  stageConfigurationId: string,
  {
    stageName,
    experimentalConditions,
    stageConfigurationIdForEntryTemplate,
  }: {
    stageName: string;
    experimentalConditions: Array<ExperimentalCondition>;
    stageConfigurationIdForEntryTemplate: string | null;
  }
): void {
  LoadingScreen.show();
  WorkflowTemplateDAO.editStageConfiguration(stageConfigurationId, {
    stageName,
    experimentalConditions,
    stageConfigurationIdForEntryTemplate,
  })
    .done((response: WorkflowTemplateDAO.EditStageConfigurationResponse) => {
      const {
        workflowTemplateVersion,
        stageConfigurations,
        stageConfiguration,
        stageFlowNodes,
        workflowDetails,
        deletedEntryIds,
      } = response;
      if (workflowDetails) {
        const {stageRuns, entryIdToStructuredData} = workflowDetails;
        getStore().dispatch({
          type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED,
          ...workflowDetails,
        });
        StageRunActions.dispatchStageRunsFetchedAndFetchMissingSamples({
          stageRuns: Object.values(stageRuns),
          entryIdToStructuredData,
        });
      }
      getStore().dispatch({
        type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED,
        workflowTemplateVersion,
        stageConfigurations,
        stageFlowNodes,
      });
      getStore().dispatch({
        type: WorkflowTemplateActionType.WORKFLOW_STAGE_CONFIGURATION_FETCH_COMPLETED,
        stageConfiguration,
      });
      FileBrowserActions.bulkRemoveFromWorkspace(deletedEntryIds);
    })
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function reorderStageConfiguration(stageConfigurationId: string, newIndex: number): void {
  LoadingScreen.show();
  WorkflowTemplateDAO.reorderStageConfiguration(stageConfigurationId, newIndex)
    .done(
      ({workflowTemplateVersion, stageConfigurations, stageFlowNodes}: WorkflowTemplateVersionDetails) => {
        getStore().dispatch({
          type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED,
          workflowTemplateVersion,
          stageConfigurations,
          stageFlowNodes,
        });
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

/**
 * Create a workflow template and return the xhr.
 * Handles success, but not failures. It is the responsibility of the caller to handle failures
 */
export function createWorkflowTemplate({
  name,
  owner,
  ownerInfo,
  prefix,
  collaborators,
  workflowTemplateVersionId,
}: {
  name: string;
  owner: Owner;
  ownerInfo: OwnerInfo;
  prefix: string;
  collaborators: Array<DataCollaborator>;
  workflowTemplateVersionId?: string;
}): JQueryPromise<WorkflowTemplateDAO.FetchWorkflowTemplateResponse> {
  return WorkflowTemplateDAO.createWorkflowTemplate({
    name,
    owner: owner.handle,
    prefix,
    memberDataPolicyId: ownerInfo.memberDataPolicyId,
    collaborators,
    workflowTemplateVersionId,
  }).done(({workflowTemplate}: {workflowTemplate: WorkflowTemplate}) => {
    const versions = workflowTemplate.workflowTemplateVersions;
    assert(versions.length === 1, 'Expected exactly one version in a new workflow template.');
    getStore().dispatch({
      type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_FULL_FETCH_COMPLETED,
      workflowTemplate,
    });
    openWorkflowTemplateViewScreen(workflowTemplate.id);
  });
}

/**
 * Deletes the workflow template and return the xhr.
 * Handles success, but not failures. It is the responsibility of the caller to handle failures
 */
export function deleteWorkflowTemplate(workflowTemplateId: string): JQueryPromise<{}> {
  return WorkflowTemplateDAO.deleteWorkflowTemplate(workflowTemplateId).done(() => {
    WorkflowActions.openWorkflowDashboardTemplatesScreen();
  });
}

/**
 * Edits the workflow template and return the xhr.
 * Handles success, but not failures. It is the responsibility of the caller to handle failures
 */
export function editWorkflowTemplateMetadata(
  workflowTemplateId: string,
  {name, owner, ownerInfo, prefix, collaborators}: Ownable
): JQueryPromise<WorkflowTemplateDAO.EditWorkflowTemplateMetadataResponse> {
  return WorkflowTemplateDAO.editWorkflowTemplateMetadata(workflowTemplateId, {
    name,
    owner: owner.handle,
    prefix,
    ownerInfo,
    collaborators,
  }).done((response: WorkflowTemplateDAO.EditWorkflowTemplateMetadataResponse) => {
    getStore().dispatch({
      type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_FULL_FETCH_COMPLETED,
      workflowTemplate: response.workflowTemplate,
    });
    const collaboratorsByOwnableId: {[ownableId: string]: Array<Collaborator>} = {};
    collaboratorsByOwnableId[workflowTemplateId] = response.collaborators;
    getStore().dispatch(ModelActions.collaboratorsFetchCompleted(collaboratorsByOwnableId));
  });
}

export function editWorkflowTemplateVersion(
  publishedWorkflowTemplateVersion: WorkflowTemplateVersion & {workflowTemplate: WorkflowTemplateSummary}
): void {
  LoadingScreen.show();
  WorkflowTemplateDAO.createWorkflowTemplateVersion(
    publishedWorkflowTemplateVersion.workflowTemplate.id,
    publishedWorkflowTemplateVersion.id
  )
    .done(
      ({
        workflowTemplate,
        workflowTemplateVersion,
        stageConfigurations,
        stageFlowNodes,
      }: WorkflowTemplateDAO.CreateWorkflowTemplateVersionResponse) => {
        getStore().dispatch({
          type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_FULL_FETCH_COMPLETED,
          workflowTemplate,
        });
        getStore().dispatch({
          type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED,
          workflowTemplateVersion,
          stageConfigurations,
          stageFlowNodes,
        });
        getStore().dispatch({
          type: WorkflowTemplateActionType.WORKFLOW_SELECT_WORKFLOW_TEMPLATE_VERSION,
          workflowTemplateVersionId: workflowTemplateVersion.id,
        });
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function publishDraft(workflowTemplateVersionId: string): void {
  LoadingScreen.show();
  WorkflowTemplateDAO.publishDraft(workflowTemplateVersionId)
    .done(
      ({
        workflowTemplateVersion,
        stageConfigurations,
        stageFlowNodes,
      }: WorkflowTemplateVersionDetails & {
        workflowTemplateVersion: WorkflowTemplateVersion & {workflowTemplate: WorkflowTemplateSummary};
      }) => {
        getStore().dispatch({
          type: WorkflowTemplateActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED,
          workflowTemplateVersion,
          stageConfigurations,
          stageFlowNodes,
        });
        notifications.success(`Published version ${workflowTemplateVersion.version} of
                             ${workflowTemplateVersion.workflowTemplate.name}`);
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function fetchCollaborators(workflowTemplateId: string, {onFailure}: {onFailure?: () => void}): void {
  LoadingScreen.show();
  WorkflowTemplateDAO.fetchCollaborators(workflowTemplateId)
    .done((collaborators: Array<Collaborator>) => {
      const collaboratorsByOwnableId: {[ownableId: string]: Array<Collaborator>} = {};
      collaboratorsByOwnableId[workflowTemplateId] = collaborators;
      getStore().dispatch(ModelActions.collaboratorsFetchCompleted(collaboratorsByOwnableId));
    })
    .fail((error) => {
      notifications.notifyError(error);
      if (onFailure) {
        onFailure();
      }
    })
    .always(() => {
      LoadingScreen.hide();
    });
}

export type WorkflowTemplateAction =
  | WorkflowTemplateViewScreenOpenAction
  | WorkflowTemplateVersionDetailsFetchCompletedAction
  | WorkflowTemplateVersionSummaryFetchCompletedAction
  | SelectWorkflowTemplateVersionAction
  | WorkflowTemplateFullFetchCompletedAction
  | AllWorkflowTemplateFetchCompletedAction
  | WorkflowDetailsFetchCompletedAction
  | StageConfigurationFetchCompletedAction;
